import type { ResumeProcessorProps } from '@mf-types/remote/compiled-types/components/ResumeProcessor/ResumeProcessor';

type ConnectionField = 'category' | 'category_id';

// Custom Error Classes for better error handling
class InvalidConnectionFieldError extends Error {
  constructor(field: ConnectionField) {
    super(`Invalid connection field: ${field}. Must be 'category' or 'category_id'`);
    this.name = 'InvalidConnectionFieldError';
  }
}

class MissingFieldError extends Error {
  constructor(field: ConnectionField, sectionIndex: number) {
    super(`Missing or invalid ${field} field in section at index ${sectionIndex}`);
    this.name = 'MissingFieldError';
  }
}

/**
 * Validates that all sections have the required connection field
 * Think of this as checking that all library sections have proper labels
 */
function validateSections(
  sections: ResumeProcessorProps['categorizedCredits'],
  connectionField: ConnectionField,
  catalogName: string,
): void {
  sections.forEach((section, index) => {
    const fieldValue = section[connectionField];
    if (!fieldValue || typeof fieldValue !== 'string' || fieldValue.trim() === '') {
      throw new MissingFieldError(connectionField, index);
    }
  });
}

/**
 * Creates a lookup index for fast section matching
 * Analogy: Creating a card catalog index for quick book lookup
 */
function createSectionIndex(
  sections: ResumeProcessorProps['categorizedCredits'],
  connectionField: ConnectionField,
): Map<string, ResumeProcessorProps['categorizedCredits']> {
  const index = new Map<string, ResumeProcessorProps['categorizedCredits']>();

  sections.forEach((section) => {
    const key = section[connectionField];
    index.set(key, section);
  });

  return index;
}

/**
 * Merges two category catalogs based on the specified connection field
 *
 * Domain Logic:
 * - Primary catalog structure is preserved (like keeping original library organization)
 * - Secondary catalog credits are appended to matching sections
 * - New sections from secondary catalog are added at the end
 * - No deduplication is performed (assuming unique UUIDs)
 *
 * @param primaryCatalog - The base catalog to merge into
 * @param secondaryCatalog - The catalog to merge from
 * @param connectionField - Field to use for matching ('category' or 'category_id')
 * @returns New merged catalog array
 */
function mergeCatalogSections(
  primaryCatalog: ResumeProcessorProps['categorizedCredits'],
  secondaryCatalog: ResumeProcessorProps['categorizedCredits'],
  connectionField: ConnectionField,
): ResumeProcessorProps['categorizedCredits'] {
  // Runtime validation
  if (!['category', 'category_id'].includes(connectionField)) {
    throw new InvalidConnectionFieldError(connectionField);
  }

  // Validate both catalogs have required fields
  validateSections(primaryCatalog, connectionField, 'primary');
  validateSections(secondaryCatalog, connectionField, 'secondary');

  // Create lookup index for secondary catalog (like a card catalog)
  const secondaryIndex = createSectionIndex(secondaryCatalog, connectionField);

  // Create result array starting with deep copy of primary catalog
  const mergedCatalog: ResumeProcessorProps['categorizedCredits'] = primaryCatalog.map((section) => ({
    ...section,
    credits: [...section.credits], // Deep copy credits array
  }));

  // Track which secondary sections we've processed
  const processedSecondaryKeys = new Set<string>();

  // Phase 1: Merge existing sections (append credits from secondary to primary)
  mergedCatalog.forEach((primarySection) => {
    const connectionKey = primarySection[connectionField];
    const matchingSecondarySection = secondaryIndex.get(connectionKey);

    if (matchingSecondarySection) {
      // Append secondary credits to primary section
      primarySection.credits.push(...matchingSecondarySection.credits);
      processedSecondaryKeys.add(connectionKey);
    }
  });

  // Phase 2: Add new sections from secondary catalog
  secondaryCatalog.forEach((secondarySection) => {
    const connectionKey = secondarySection[connectionField];

    if (!processedSecondaryKeys.has(connectionKey) && secondarySection.credits.length !== 0) {
      // This is a new section, add it to the merged catalog
      mergedCatalog.push({
        ...secondarySection,
        credits: [...secondarySection.credits], // Deep copy
      });
    }
  });

  return mergedCatalog;
}

export default mergeCatalogSections;
